import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { projects, type ProjectSummary, ApiError } from '../api.js';
import { WelcomeBanner } from '../components/welcome-banner.js';
import { LoadingBlock } from '../components/ui/spinner.js';

export function ProjectsListPage() {
  const [list, setList] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    try {
      const r = await projects.list();
      setList(r.projects);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
      setList([]);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Projects</h1>
          <p className="text-muted text-sm">Each project is one script, directed once, rendered as many ways as you want.</p>
        </div>
        <Link
          to="/projects/new"
          className="bg-ink text-parchment px-4 py-2 rounded-md hover:bg-ink/90 transition-colors text-sm"
        >
          New project
        </Link>
      </div>

      <WelcomeBanner visible={!!(list && list.length === 1 && list[0]?.title === 'Welcome to Scriptvoice')} />
      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {list === null ? (
        <LoadingBlock label="Loading projects…" />
      ) : list.length === 0 ? (
        <div className="border border-soft rounded-lg p-12 text-center">
          <p className="text-muted mb-4">No projects yet.</p>
          <Link
            to="/projects/new"
            className="text-accent underline underline-offset-4 hover:no-underline"
          >
            Start your first one
          </Link>
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((p) => (
            <li key={p.id}>
              <Link
                to={`/projects/${p.id}`}
                className="card card-hover block px-5 py-4 bg-white"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <h2 className="font-serif text-lg truncate">{p.title}</h2>
                  <span className="text-xs text-muted whitespace-nowrap">
                    {fmtRel(p.updated_at)}
                  </span>
                </div>
                <div className="text-xs text-muted/80 mt-1.5 flex items-center gap-3 flex-wrap">
                  <span>{p.word_count.toLocaleString()} words</span>
                  <Dot />
                  <span className={p.has_ir ? 'text-ink/70' : ''}>
                    {p.has_ir ? 'directed' : 'not yet directed'}
                  </span>
                  {p.last_render_provider && (
                    <>
                      <Dot />
                      <span>
                        rendered via {p.last_render_provider} ({p.last_render_voice_id})
                      </span>
                    </>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Dot() {
  return <span className="text-muted/30">·</span>;
}

function fmtRel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
